import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

// ─── JSON-LD ──────────────────────────────────────────────────

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'O que é mapa natal?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'O mapa natal é uma fotografia do céu no exato momento e local do seu nascimento. Ele mostra a posição do Sol, da Lua e dos planetas nos signos e casas astrológicas, revelando tendências de personalidade, talentos, desafios e potenciais ao longo da vida.',
      },
    },
    {
      '@type': 'Question',
      name: 'Preciso saber a hora exata de nascimento para calcular o mapa natal?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'A hora de nascimento é fundamental para posicionar corretamente o Ascendente e as 12 casas astrológicas. Sem ela, o mapa ainda mostra as posições planetárias nos signos, mas perde a precisão das casas, que indicam as áreas da vida afetadas por cada planeta.',
      },
    },
    {
      '@type': 'Question',
      name: 'Qual a diferença entre mapa natal e horóscopo?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'O horóscopo é uma previsão genérica baseada apenas no signo solar, escrita para grupos de pessoas. O mapa natal é único para cada indivíduo, é uma análise completa de todos os planetas, casas e aspectos no momento exato do nascimento, muito mais precisa e personalizada.',
      },
    },
    {
      '@type': 'Question',
      name: 'Quanto tempo leva para interpretar um mapa natal?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Uma interpretação básica pode ser feita em minutos com ferramentas digitais. Uma leitura profunda e integrada, considerando todos os planetas, casas, aspectos e seus temas de vida, pode levar horas. Na Professya, nossa IA sintetiza o mapa completo em linguagem clara e acessível.',
      },
    },
    {
      '@type': 'Question',
      name: 'O mapa natal muda ao longo da vida?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'O mapa natal em si é fixo, ele representa o céu no momento do seu nascimento e não muda. O que muda são os trânsitos: os movimentos dos planetas pelo céu atual ativam diferentes pontos do seu mapa, criando ciclos e oportunidades ao longo da vida.',
      },
    },
  ],
};

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://professya.com' },
    { '@type': 'ListItem', position: 2, name: 'Mapa Natal', item: 'https://professya.com/mapa-natal' },
  ],
};

// ─── Component ───────────────────────────────────────────────

export function MapaNatalPage() {
  return (
    <>
      <Helmet>
        <title>O que é Mapa Natal: Como Calcular e Interpretar, Professya</title>
        <meta name="description" content="Aprenda o que é mapa natal, como calcular e interpretar sua carta astral completa. Descubra planetas, casas e aspectos do seu mapa natal único." />
        <meta property="og:title" content="O que é Mapa Natal: Como Calcular e Interpretar, Professya" />
        <meta property="og:description" content="Aprenda o que é mapa natal, como calcular e interpretar sua carta astral completa. Descubra planetas, casas e aspectos do seu mapa natal único." />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://professya.com/mapa-natal" />
        <link rel="canonical" href="https://professya.com/mapa-natal" />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      <div style={{ minHeight: '100vh', backgroundColor: 'var(--bg-base)', color: 'var(--text-primary)' }}>

        {/* Hero */}
        <div style={{ background: 'linear-gradient(180deg, rgba(245,200,66,0.08) 0%, transparent 100%)', padding: '3rem 1.5rem 2rem', textAlign: 'center' }}>
          <Link to="/" style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)', textDecoration: 'none', display: 'inline-block', marginBottom: '1.5rem' }}>
            ✦ Professya
          </Link>
          <div style={{ fontSize: '3.5rem', marginBottom: '0.75rem' }}>🌌</div>
          <h1 className="font-display" style={{ fontSize: 'clamp(2rem,6vw,3rem)', color: 'var(--text-primary)', marginBottom: '0.75rem' }}>
            O que é o Mapa Natal?
          </h1>
          <p style={{ color: 'var(--text-secondary)', maxWidth: '640px', margin: '0 auto', lineHeight: 1.7, fontSize: 'var(--text-body)' }}>
            Guia completo sobre carta astral, o que revela, como calcular e como interpretar cada elemento do seu mapa natal único.
          </p>
        </div>

        <div style={{ maxWidth: '760px', margin: '0 auto', padding: '0 1.5rem 4rem' }}>

          {/* Section 1 */}
          <section style={{ marginBottom: '2.5rem' }}>
            <h2 className="font-display" style={{ fontSize: '1.375rem', marginBottom: '0.875rem', color: 'var(--petrol)' }}>
              O que é o mapa natal
            </h2>
            <p style={{ color: 'var(--text-secondary)', lineHeight: 1.85 }}>
              O mapa natal, também chamado de carta astral ou tema natal, é uma representação circular do céu no exato instante e lugar do seu nascimento. Ele registra as posições do Sol, da Lua e dos oito planetas nos doze signos do zodíaco, distribuídos pelas doze casas astrológicas. Imagine uma fotografia do cosmos tirada especificamente para você: cada planeta ocupava um ponto preciso do céu, e esse padrão único é o ponto de partida para compreender sua psicologia, seus talentos naturais, seus desafios recorrentes e os ciclos de vida que você atravessará. Não se trata de determinismo, o mapa natal é uma linguagem simbólica que revela potenciais e padrões, não um destino fixo e imutável.
            </p>
          </section>

          {/* Section 2 */}
          <section style={{ marginBottom: '2.5rem' }}>
            <h2 className="font-display" style={{ fontSize: '1.375rem', marginBottom: '0.875rem' }}>
              Por que o mapa natal é único para cada pessoa
            </h2>
            <p style={{ color: 'var(--text-secondary)', lineHeight: 1.85 }}>
              Dois gêmeos nascidos com minutos de diferença podem ter mapas natais ligeiramente distintos, especialmente no Ascendente e nas casas. Isso ocorre porque a Terra gira aproximadamente um grau a cada quatro minutos, deslocando o ponto de partida de todas as casas. Para pessoas nascidas em cidades diferentes, mesmo no mesmo dia e hora, as casas também se reorganizam. É essa combinação específica de data, hora e local que torna cada mapa natal verdadeiramente singular. Com mais de 40.000 posições possíveis de planetas nos signos e casas, a probabilidade de duas pessoas terem o mesmo mapa é astronomicamente pequena, o que faz da astrologia natal uma das ferramentas mais personalizadas de autoconhecimento.
            </p>
          </section>

          {/* Section 3 */}
          <section style={{ marginBottom: '2.5rem' }}>
            <h2 className="font-display" style={{ fontSize: '1.375rem', marginBottom: '0.875rem' }}>
              O que o mapa natal revela
            </h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
              {[
                { icon: '🪐', title: 'Planetas', desc: 'Cada planeta representa uma energia ou função psicológica: o Sol é identidade, a Lua é emoção, Mercúrio é comunicação, Vênus é amor, Marte é ação.' },
                { icon: '🏠', title: 'Casas', desc: 'As 12 casas indicam as áreas da vida: amor, carreira, família, finanças, saúde, espiritualidade. Cada planeta em uma casa mostra onde essa energia se expressa.' },
                { icon: '📐', title: 'Aspectos', desc: 'São os ângulos entre planetas, trígonos, quadraturas, oposições. Revelam como as energias interagem, criando facilidades ou tensões criativas na personalidade.' },
                { icon: '⬆️', title: 'Ascendente e Casas', desc: 'O Ascendente define a cúspide da Casa 1 e organiza todas as demais. É a chave que transforma posições planetárias em um mapa verdadeiramente individual.' },
              ].map(item => (
                <div key={item.title} className="card">
                  <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>{item.icon}</div>
                  <h3 style={{ fontSize: 'var(--text-label)', fontWeight: 600, marginBottom: '0.4rem' }}>{item.title}</h3>
                  <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)', lineHeight: 1.65 }}>{item.desc}</p>
                </div>
              ))}
            </div>
            <p style={{ color: 'var(--text-secondary)', lineHeight: 1.85 }}>
              Além dos planetas visíveis, o mapa natal pode incluir Quiron (feridas e cura), os Nodos Lunares (karma e propósito de vida) e asteroides como Ceres e Juno, que aprofundam temas de nutrição, relacionamentos e identidade feminina.
            </p>
          </section>

          {/* Section 4 */}
          <section style={{ marginBottom: '2.5rem' }}>
            <h2 className="font-display" style={{ fontSize: '1.375rem', marginBottom: '0.875rem' }}>
              Como interpretar seu mapa natal
            </h2>
            <p style={{ color: 'var(--text-secondary)', lineHeight: 1.85 }}>
              A interpretação começa pelo trinômio Sol-Lua-Ascendente: o núcleo da identidade (Sol), o mundo emocional e necessidades inconscientes (Lua) e a expressão externa e imagem pública (Ascendente). Em seguida, analisa-se onde cada planeta está posicionado, em qual signo e em qual casa, e quais aspectos forma com os outros planetas. Um Marte em Áries na Casa 10, por exemplo, sugere ambição, iniciativa e liderança na carreira. Aspectos entre planetas mostram diálogos internos: uma quadratura Sol-Saturno pode indicar conflito entre autoexpressão e senso de dever, enquanto um trígono Vênus-Júpiter aponta facilidade para abundância e relacionamentos harmoniosos. A síntese de todas essas camadas cria um retrato psicológico profundo e multidimensional.
            </p>
          </section>

          {/* FAQ */}
          <section style={{ marginBottom: '2.5rem' }}>
            <h2 className="font-display" style={{ fontSize: '1.375rem', marginBottom: '1.25rem' }}>
              Perguntas frequentes sobre mapa natal
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {faqSchema.mainEntity.map((item, i) => (
                <details key={i} className="card" style={{ cursor: 'pointer' }}>
                  <summary style={{ fontWeight: 600, fontSize: 'var(--text-label)', color: 'var(--text-primary)', listStyle: 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem' }}>
                    {item.name}
                    <span style={{ color: 'var(--petrol)', flexShrink: 0 }}>+</span>
                  </summary>
                  <p style={{ color: 'var(--text-secondary)', lineHeight: 1.8, marginTop: '0.75rem', fontSize: 'var(--text-label)' }}>
                    {item.acceptedAnswer.text}
                  </p>
                </details>
              ))}
            </div>
          </section>

          {/* CTA */}
          <div className="card" style={{ textAlign: 'center', background: 'linear-gradient(135deg, rgba(245,200,66,0.06) 0%, transparent 100%)', border: '1px solid rgba(245,200,66,0.2)' }}>
            <div style={{ fontSize: '2rem', marginBottom: '0.75rem' }}>🌌</div>
            <p className="font-display" style={{ fontSize: '1.125rem', marginBottom: '0.5rem' }}>
              Pronto para conhecer o seu mapa?
            </p>
            <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-label)', marginBottom: '1.25rem' }}>
              Calcule seu mapa natal completo gratuitamente e receba uma interpretação personalizada com IA.
            </p>
            <Link to="/register" className="btn btn-primary">
              Calcule seu Mapa Natal Completo Gratuitamente →
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}
